import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ThemeToggle from '../components/ThemeToggle';

const { 
  FiSearch, FiUsers, FiShield, FiSettings, FiArrowLeft, FiChevronDown, 
  FiChevronRight, FiClock, FiCreditCard, FiPackage, FiPhone, FiMail,
  FiCheckCircle, FiAlertTriangle, FiInfo
} = FiIcons;

function HowItWorks() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedItems, setExpandedItems] = useState({});

  const toggleExpanded = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const helpArticles = [
    // Visitor Guides
    {
      id: 'visitor-getting-started',
      category: 'visitor',
      title: 'Getting Started as a Visitor',
      description: 'Complete guide to placing your first order',
      tags: ['beginner', 'setup', 'first-time'],
      content: `
        <h3>Welcome to AB Orders</h3>
        <p>AB Orders makes prison visits more efficient by allowing you to pre-order food and beverages before your visit. This guide will walk you through your first order.</p>
        
        <h4>Step 1: Account Setup</h4>
        <p>1. Visit the AB Orders website and click "Login"</p>
        <p>2. Select "Visitor" as your account type</p>
        <p>3. Enter your email and create a secure password</p>
        <p>4. Verify your email address (in live system)</p>
        
        <h4>Step 2: Prison Selection</h4>
        <p>1. Choose the prison you'll be visiting from our approved facilities list</p>
        <p>2. Each prison has its own menu and policies</p>
        <p>3. Ensure your chosen prison participates in the AB Orders program</p>
        
        <h4>Step 3: Visit Details</h4>
        <p>1. Enter your full name as registered with the prison</p>
        <p>2. Provide your PO (Prison Officer) Visiting Number</p>
        <p>3. Enter the prisoner's full name and prisoner number</p>
        <p>4. Select your visit date and time slot</p>
        
        <h4>Step 4: Browse Menu</h4>
        <p>1. View available items categorized by type</p>
        <p>2. Check stock levels and prices</p>
        <p>3. Read item descriptions and dietary information</p>
        <p>4. Add items to your cart with desired quantities</p>
        
        <h4>Step 5: Checkout</h4>
        <p>1. Review your order carefully</p>
        <p>2. Confirm visit details are correct</p>
        <p>3. Process payment securely online</p>
        <p>4. Receive order confirmation and receipt</p>
        
        <div class="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg mt-4">
          <strong>Important:</strong> Orders must be placed at least 24 hours before your visit. Bring your order confirmation when you arrive at the prison.
        </div>
      `
    },
    {
      id: 'visitor-payment-methods',
      category: 'visitor',
      title: 'Payment Methods & Security',
      description: 'Understanding secure payment options',
      tags: ['payment', 'security', 'billing'],
      content: `
        <h3>Secure Payment Processing</h3>
        <p>AB Orders uses bank-level encryption to protect your payment information. All transactions are processed through government-approved payment gateways.</p>
        
        <h4>Accepted Payment Methods</h4>
        <ul>
          <li><strong>Debit Cards:</strong> Visa, Mastercard, Maestro</li>
          <li><strong>Credit Cards:</strong> Visa, Mastercard, American Express</li>
          <li><strong>Digital Wallets:</strong> PayPal, Apple Pay, Google Pay</li>
          <li><strong>Bank Transfer:</strong> Direct bank transfers for larger orders</li>
        </ul>
        
        <h4>Payment Security Features</h4>
        <ul>
          <li>256-bit SSL encryption for all transactions</li>
          <li>PCI DSS compliant payment processing</li>
          <li>Two-factor authentication available</li>
          <li>Fraud detection and prevention systems</li>
          <li>No card details stored on our servers</li>
        </ul>
        
        <h4>Refund Policy</h4>
        <p><strong>Full Refunds:</strong> Available up to 48 hours before your visit</p>
        <p><strong>Partial Refunds:</strong> Available for cancelled items up to 24 hours before visit</p>
        <p><strong>Emergency Refunds:</strong> Available for medical emergencies or visit cancellations</p>
        
        <div class="bg-green-50 dark:bg-green-900 p-4 rounded-lg mt-4">
          <strong>Tip:</strong> Save your payment method for faster checkout on future visits. Your information is securely encrypted and never shared.
        </div>
      `
    },
    {
      id: 'visitor-order-modifications',
      category: 'visitor',
      title: 'Modifying or Cancelling Orders',
      description: 'How to change your order after placement',
      tags: ['modification', 'cancellation', 'changes'],
      content: `
        <h3>Order Modification Guidelines</h3>
        <p>AB Orders allows flexibility in managing your orders, but timing restrictions apply to ensure fresh food preparation.</p>
        
        <h4>Modification Time Limits</h4>
        <ul>
          <li><strong>Full Modifications:</strong> Up to 48 hours before visit</li>
          <li><strong>Item Additions:</strong> Up to 24 hours before visit</li>
          <li><strong>Item Removals:</strong> Up to 24 hours before visit</li>
          <li><strong>Emergency Changes:</strong> Contact prison directly</li>
        </ul>
        
        <h4>How to Modify Your Order</h4>
        <p>1. Log into your AB Orders account</p>
        <p>2. Go to "My Orders" section</p>
        <p>3. Find your upcoming order</p>
        <p>4. Click "Modify Order" if within time limit</p>
        <p>5. Make your changes and confirm</p>
        <p>6. Pay any additional charges or receive refunds</p>
        
        <h4>Cancellation Process</h4>
        <p>1. Visit your order history</p>
        <p>2. Select the order to cancel</p>
        <p>3. Choose cancellation reason</p>
        <p>4. Confirm cancellation request</p>
        <p>5. Receive refund within 3-5 business days</p>
        
        <h4>Special Circumstances</h4>
        <ul>
          <li><strong>Visit Cancelled by Prison:</strong> Automatic full refund</li>
          <li><strong>Medical Emergency:</strong> Full refund with documentation</li>
          <li><strong>Weather Closures:</strong> Automatic rescheduling or refund</li>
          <li><strong>System Maintenance:</strong> Orders automatically held</li>
        </ul>
        
        <div class="bg-yellow-50 dark:bg-yellow-900 p-4 rounded-lg mt-4">
          <strong>Note:</strong> Late cancellations (less than 24 hours) may incur a processing fee to cover food preparation costs.
        </div>
      `
    },
    {
      id: 'visitor-collection-process',
      category: 'visitor',
      title: 'Collecting Your Order',
      description: 'What to expect when you arrive at the prison',
      tags: ['collection', 'arrival', 'process'],
      content: `
        <h3>Order Collection Process</h3>
        <p>Your pre-ordered items will be ready for collection when you arrive for your visit. Follow these steps for a smooth experience.</p>
        
        <h4>Before You Arrive</h4>
        <ul>
          <li>Check your email for order confirmation</li>
          <li>Screenshot or print your order receipt</li>
          <li>Verify your visit time and date</li>
          <li>Check for any prison-specific notices</li>
        </ul>
        
        <h4>At Prison Reception</h4>
        <p>1. Present your ID and visit confirmation</p>
        <p>2. Show your AB Orders receipt to reception staff</p>
        <p>3. Complete standard security procedures</p>
        <p>4. Proceed to the AB Orders collection point</p>
        
        <h4>Collection Point Procedure</h4>
        <p>1. Queue at the designated AB Orders area</p>
        <p>2. Present your order confirmation to staff</p>
        <p>3. Verify your identity and order details</p>
        <p>4. Check your items against the receipt</p>
        <p>5. Sign for receipt of your order</p>
        
        <h4>Taking Items to Visit</h4>
        <ul>
          <li>Items will be provided in approved containers</li>
          <li>All packaging meets prison security standards</li>
          <li>Take items directly to your visit</li>
          <li>Unused items cannot be taken home</li>
        </ul>
        
        <h4>If There's a Problem</h4>
        <ul>
          <li><strong>Missing Items:</strong> Report immediately to collection staff</li>
          <li><strong>Wrong Order:</strong> Check order number and contact supervisor</li>
          <li><strong>Quality Issues:</strong> Request replacement or refund</li>
          <li><strong>Late Arrival:</strong> Orders held for 2 hours past visit time</li>
        </ul>
        
        <div class="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg mt-4">
          <strong>Tip:</strong> Arrive 15 minutes early to allow time for order collection before your scheduled visit begins.
        </div>
      `
    },
    // Prison Staff Guides
    {
      id: 'prison-getting-started',
      category: 'prison',
      title: 'Prison Staff Getting Started Guide',
      description: 'Complete setup and daily operations guide',
      tags: ['setup', 'training', 'operations'],
      content: `
        <h3>AB Orders for Prison Staff</h3>
        <p>This comprehensive guide will help prison staff understand and manage the AB Orders system effectively.</p>
        
        <h4>System Access Setup</h4>
        <p>1. Contact your facility administrator for AB Orders access</p>
        <p>2. Complete the mandatory training module</p>
        <p>3. Receive your unique login credentials</p>
        <p>4. Set up two-factor authentication</p>
        <p>5. Complete the system orientation checklist</p>
        
        <h4>Daily Operations Overview</h4>
        <ul>
          <li><strong>Morning Setup:</strong> Check overnight orders and inventory</li>
          <li><strong>Order Monitoring:</strong> Track incoming orders throughout the day</li>
          <li><strong>Inventory Management:</strong> Update stock levels and availability</li>
          <li><strong>Order Fulfillment:</strong> Prepare and package orders</li>
          <li><strong>Collection Management:</strong> Assist visitors with order pickup</li>
        </ul>
        
        <h4>Key Responsibilities</h4>
        <ul>
          <li>Monitor and approve incoming orders</li>
          <li>Maintain accurate inventory records</li>
          <li>Ensure food safety and hygiene standards</li>
          <li>Handle visitor collection queries</li>
          <li>Report system issues promptly</li>
          <li>Complete daily reconciliation reports</li>
        </ul>
        
        <h4>Security Protocols</h4>
        <ul>
          <li>Verify visitor identity before order release</li>
          <li>Check order confirmations against system records</li>
          <li>Report suspicious activities immediately</li>
          <li>Follow contraband screening procedures</li>
          <li>Maintain visitor confidentiality</li>
        </ul>
        
        <h4>Emergency Procedures</h4>
        <ul>
          <li><strong>System Downtime:</strong> Use backup paper systems</li>
          <li><strong>Food Safety Issues:</strong> Quarantine affected items immediately</li>
          <li><strong>Security Concerns:</strong> Follow lockdown procedures</li>
          <li><strong>Visitor Complaints:</strong> Escalate to supervisor</li>
        </ul>
        
        <div class="bg-red-50 dark:bg-red-900 p-4 rounded-lg mt-4">
          <strong>Important:</strong> All staff must complete monthly security briefings and maintain current food hygiene certificates.
        </div>
      `
    },
    {
      id: 'prison-inventory-management',
      category: 'prison',
      title: 'Inventory Management System',
      description: 'Managing stock levels and menu items',
      tags: ['inventory', 'stock', 'menu'],
      content: `
        <h3>Inventory Management Best Practices</h3>
        <p>Effective inventory management ensures order fulfillment and maintains visitor satisfaction while minimizing waste.</p>
        
        <h4>Daily Inventory Tasks</h4>
        <ul>
          <li><strong>Morning Stock Check:</strong> Verify overnight deliveries</li>
          <li><strong>System Updates:</strong> Update stock levels in real-time</li>
          <li><strong>Expiry Monitoring:</strong> Check and rotate stock by dates</li>
          <li><strong>Reorder Points:</strong> Monitor low stock alerts</li>
          <li><strong>End-of-Day Count:</strong> Reconcile physical vs system stock</li>
        </ul>
        
        <h4>Adding New Menu Items</h4>
        <p>1. Navigate to Inventory Management section</p>
        <p>2. Click "Add New Item"</p>
        <p>3. Complete item details:</p>
        <ul>
          <li>Item name and description</li>
          <li>Category classification</li>
          <li>Price per unit</li>
          <li>Initial stock quantity</li>
          <li>Supplier information</li>
          <li>Allergen warnings</li>
          <li>Nutritional information</li>
        </ul>
        <p>4. Upload high-quality product image</p>
        <p>5. Set minimum stock alerts</p>
        <p>6. Activate item for ordering</p>
        
        <h4>Stock Level Management</h4>
        <ul>
          <li><strong>Green Status:</strong> Stock above minimum threshold</li>
          <li><strong>Amber Status:</strong> Stock approaching minimum level</li>
          <li><strong>Red Status:</strong> Critical stock level reached</li>
          <li><strong>Out of Stock:</strong> Item automatically disabled for ordering</li>
        </ul>
        
        <h4>Automated Reordering</h4>
        <p>1. Set minimum stock levels for each item</p>
        <p>2. Configure automatic supplier notifications</p>
        <p>3. Set preferred order quantities</p>
        <p>4. Schedule regular delivery slots</p>
        <p>5. Monitor supplier performance metrics</p>
        
        <h4>Quality Control Procedures</h4>
        <ul>
          <li>Check delivery quality upon receipt</li>
          <li>Verify expiry dates and batch numbers</li>
          <li>Store items according to food safety guidelines</li>
          <li>Implement FIFO (First In, First Out) rotation</li>
          <li>Document any quality issues</li>
        </ul>
        
        <div class="bg-green-50 dark:bg-green-900 p-4 rounded-lg mt-4">
          <strong>Tip:</strong> Use the inventory analytics dashboard to identify popular items and optimize your menu offerings.
        </div>
      `
    },
    {
      id: 'prison-order-processing',
      category: 'prison',
      title: 'Order Processing & Fulfillment',
      description: 'Step-by-step order handling procedures',
      tags: ['orders', 'fulfillment', 'processing'],
      content: `
        <h3>Order Processing Workflow</h3>
        <p>Efficient order processing ensures timely preparation and visitor satisfaction. Follow these procedures for consistent service.</p>
        
        <h4>Order Reception</h4>
        <p>1. Orders appear in the system immediately upon visitor payment</p>
        <p>2. Review new orders every 2 hours during business hours</p>
        <p>3. Check for special requirements or dietary needs</p>
        <p>4. Verify stock availability for all items</p>
        <p>5. Flag any potential issues immediately</p>
        
        <h4>Order Preparation Timeline</h4>
        <ul>
          <li><strong>48+ hours before visit:</strong> Order planning and procurement</li>
          <li><strong>24 hours before:</strong> Final stock verification</li>
          <li><strong>4 hours before:</strong> Begin food preparation</li>
          <li><strong>2 hours before:</strong> Complete packaging</li>
          <li><strong>1 hour before:</strong> Final quality checks</li>
        </ul>
        
        <h4>Food Preparation Standards</h4>
        <ul>
          <li>Follow all HACCP guidelines</li>
          <li>Maintain cold chain for perishables</li>
          <li>Use approved ingredients only</li>
          <li>Document preparation times and temperatures</li>
          <li>Label items with preparation and consumption times</li>
        </ul>
        
        <h4>Packaging Requirements</h4>
        <ul>
          <li>Use only approved, security-cleared containers</li>
          <li>Ensure all packaging is tamper-evident</li>
          <li>Include visitor name and order number on each package</li>
          <li>Separate hot and cold items appropriately</li>
          <li>Include any required allergen warnings</li>
        </ul>
        
        <h4>Quality Control Checks</h4>
        <p>Before releasing any order:</p>
        <ul>
          <li>Verify all items against order confirmation</li>
          <li>Check food temperature and quality</li>
          <li>Ensure proper packaging integrity</li>
          <li>Confirm visitor and order details match</li>
          <li>Document completion in system</li>
        </ul>
        
        <h4>Problem Resolution</h4>
        <ul>
          <li><strong>Missing Stock:</strong> Contact visitor immediately with alternatives</li>
          <li><strong>Quality Issues:</strong> Replace items or offer refund</li>
          <li><strong>Late Orders:</strong> Prioritize based on visit times</li>
          <li><strong>Special Requests:</strong> Escalate to supervisor if outside policy</li>
        </ul>
        
        <div class="bg-yellow-50 dark:bg-yellow-900 p-4 rounded-lg mt-4">
          <strong>Remember:</strong> All food preparation must comply with local health authority regulations and prison security requirements.
        </div>
      `
    },
    // Admin Guides  
    {
      id: 'admin-system-overview',
      category: 'admin',
      title: 'System Administration Overview',
      description: 'Complete system management and monitoring guide',
      tags: ['administration', 'monitoring', 'management'],
      content: `
        <h3>AB Orders System Administration</h3>
        <p>This guide covers all aspects of system administration for the AB Orders platform, from user management to system monitoring.</p>
        
        <h4>Administrator Responsibilities</h4>
        <ul>
          <li>Overall system health monitoring</li>
          <li>User account management and permissions</li>
          <li>Prison facility onboarding and setup</li>
          <li>Financial reporting and reconciliation</li>
          <li>Security incident response</li>
          <li>System updates and maintenance coordination</li>
          <li>Performance optimization and scaling</li>
        </ul>
        
        <h4>Daily Administrative Tasks</h4>
        <ul>
          <li><strong>System Health Check:</strong> Review overnight alerts and logs</li>
          <li><strong>Performance Monitoring:</strong> Check response times and uptime</li>
          <li><strong>Security Review:</strong> Monitor for suspicious activities</li>
          <li><strong>Financial Reconciliation:</strong> Verify payment processing</li>
          <li><strong>Support Ticket Review:</strong> Address escalated issues</li>
          <li><strong>Backup Verification:</strong> Ensure data backups completed successfully</li>
        </ul>
        
        <h4>User Management</h4>
        <p><strong>Creating New Prison Staff Accounts:</strong></p>
        <ul>
          <li>Verify staff credentials and security clearance</li>
          <li>Create account with appropriate permissions</li>
          <li>Send secure login credentials</li>
          <li>Schedule mandatory training session</li>
          <li>Monitor initial system usage</li>
        </ul>
        
        <p><strong>Managing Visitor Accounts:</strong></p>
        <ul>
          <li>Handle account verification issues</li>
          <li>Process account deletion requests</li>
          <li>Investigate suspicious account activity</li>
          <li>Manage payment disputes</li>
        </ul>
        
        <h4>Prison Facility Management</h4>
        <ul>
          <li>Onboard new participating prisons</li>
          <li>Configure facility-specific settings</li>
          <li>Monitor facility performance metrics</li>
          <li>Coordinate system training for new facilities</li>
          <li>Handle facility-specific customizations</li>
        </ul>
        
        <h4>System Monitoring Tools</h4>
        <ul>
          <li><strong>Real-time Dashboard:</strong> System health and performance</li>
          <li><strong>Alert System:</strong> Automated notifications for critical issues</li>
          <li><strong>Analytics Platform:</strong> Usage patterns and trends</li>
          <li><strong>Security Console:</strong> Threat detection and response</li>
          <li><strong>Financial Dashboard:</strong> Revenue and transaction monitoring</li>
        </ul>
        
        <div class="bg-red-50 dark:bg-red-900 p-4 rounded-lg mt-4">
          <strong>Critical:</strong> All administrative actions are logged and audited. Maintain strict confidentiality and follow data protection protocols.
        </div>
      `
    },
    {
      id: 'admin-financial-management',
      category: 'admin',
      title: 'Financial Management & Reporting',
      description: 'Revenue tracking, reporting, and financial controls',
      tags: ['finance', 'reporting', 'revenue'],
      content: `
        <h3>Financial Management System</h3>
        <p>Comprehensive financial oversight ensures accurate revenue tracking, proper reconciliation, and regulatory compliance.</p>
        
        <h4>Revenue Streams</h4>
        <ul>
          <li><strong>Order Revenue:</strong> Direct sales from visitor orders</li>
          <li><strong>Service Fees:</strong> Processing fees for payment handling</li>
          <li><strong>Premium Services:</strong> Express ordering and priority handling</li>
          <li><strong>Partnership Revenue:</strong> Commission from supplier partnerships</li>
        </ul>
        
        <h4>Daily Financial Tasks</h4>
        <ul>
          <li>Review overnight transaction reports</li>
          <li>Reconcile payment gateway settlements</li>
          <li>Monitor refund requests and processing</li>
          <li>Check for payment failures or disputes</li>
          <li>Update financial dashboards</li>
          <li>Generate daily revenue summaries</li>
        </ul>
        
        <h4>Monthly Reporting</h4>
        <p><strong>Revenue Reports Include:</strong></p>
        <ul>
          <li>Total revenue by prison facility</li>
          <li>Revenue trends and growth analysis</li>
          <li>Top-performing menu items</li>
          <li>Average order values and frequency</li>
          <li>Refund rates and reasons</li>
          <li>Payment method preferences</li>
        </ul>
        
        <h4>Financial Controls</h4>
        <ul>
          <li><strong>Automated Reconciliation:</strong> Daily matching of orders to payments</li>
          <li><strong>Fraud Detection:</strong> AI-powered suspicious transaction monitoring</li>
          <li><strong>Multi-level Approval:</strong> Large refunds require supervisor approval</li>
          <li><strong>Audit Trail:</strong> Complete transaction history maintenance</li>
          <li><strong>Compliance Monitoring:</strong> Regulatory requirement adherence</li>
        </ul>
        
        <h4>Payment Processing Management</h4>
        <ul>
          <li>Monitor payment gateway performance</li>
          <li>Handle failed payment investigations</li>
          <li>Manage chargebacks and disputes</li>
          <li>Optimize payment routing for cost efficiency</li>
          <li>Maintain PCI compliance standards</li>
        </ul>
        
        <h4>Budgeting and Forecasting</h4>
        <ul>
          <li>Quarterly revenue projections</li>
          <li>Seasonal trend analysis</li>
          <li>Facility expansion cost modeling</li>
          <li>Technology investment planning</li>
          <li>Risk assessment and contingency planning</li>
        </ul>
        
        <div class="bg-green-50 dark:bg-green-900 p-4 rounded-lg mt-4">
          <strong>Best Practice:</strong> Set up automated alerts for unusual financial patterns or significant deviations from expected revenue targets.
        </div>
      `
    },
    // General Guides
    {
      id: 'security-privacy',
      category: 'general',
      title: 'Security & Privacy Protection',
      description: 'How we protect your data and ensure system security',
      tags: ['security', 'privacy', 'data protection'],
      content: `
        <h3>Security & Privacy Framework</h3>
        <p>AB Orders employs military-grade security measures to protect all user data and ensure the integrity of the prison visit system.</p>
        
        <h4>Data Protection Measures</h4>
        <ul>
          <li><strong>Encryption:</strong> 256-bit AES encryption for all data at rest and in transit</li>
          <li><strong>Access Controls:</strong> Multi-factor authentication and role-based permissions</li>
          <li><strong>Data Minimization:</strong> We collect only necessary information</li>
          <li><strong>Retention Policies:</strong> Automatic data deletion after retention periods</li>
          <li><strong>Regular Audits:</strong> Independent security assessments quarterly</li>
        </ul>
        
        <h4>Privacy Principles</h4>
        <ul>
          <li>Transparent data collection and usage</li>
          <li>User consent for all data processing</li>
          <li>Right to data access and deletion</li>
          <li>No data sharing without explicit consent</li>
          <li>Privacy by design in all system features</li>
        </ul>
        
        <h4>System Security Features</h4>
        <ul>
          <li><strong>Intrusion Detection:</strong> Real-time threat monitoring</li>
          <li><strong>DDoS Protection:</strong> Advanced attack mitigation</li>
          <li><strong>Security Scanning:</strong> Continuous vulnerability assessment</li>
          <li><strong>Incident Response:</strong> 24/7 security team availability</li>
          <li><strong>Compliance Monitoring:</strong> GDPR, PCI DSS, and government standards</li>
        </ul>
        
        <h4>User Security Best Practices</h4>
        <ul>
          <li>Use strong, unique passwords</li>
          <li>Enable two-factor authentication</li>
          <li>Log out completely after each session</li>
          <li>Never share login credentials</li>
          <li>Report suspicious activities immediately</li>
          <li>Keep contact information updated</li>
        </ul>
        
        <h4>Incident Reporting</h4>
        <p>If you suspect a security issue:</p>
        <ul>
          <li>Contact our security team immediately</li>
          <li>Do not attempt to investigate yourself</li>
          <li>Preserve any relevant evidence</li>
          <li>Change your password as a precaution</li>
          <li>Monitor your account for unusual activity</li>
        </ul>
        
        <div class="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg mt-4">
          <strong>Contact Security Team:</strong> security@ab-orders.com or call 0800-SECURITY for immediate assistance.
        </div>
      `
    },
    {
      id: 'troubleshooting-faq',
      category: 'general',
      title: 'Troubleshooting & FAQ',
      description: 'Common issues and their solutions',
      tags: ['troubleshooting', 'faq', 'problems'],
      content: `
        <h3>Frequently Asked Questions</h3>
        
        <h4>Account & Login Issues</h4>
        <p><strong>Q: I can't log into my account</strong></p>
        <p>A: Try these steps:</p>
        <ul>
          <li>Check your email and password are correct</li>
          <li>Ensure caps lock is off</li>
          <li>Clear your browser cache and cookies</li>
          <li>Use the "Forgot Password" feature</li>
          <li>Contact support if issues persist</li>
        </ul>
        
        <p><strong>Q: I'm not receiving verification emails</strong></p>
        <p>A: Check your spam folder and ensure emails from @ab-orders.com are allowed. Contact support to resend verification.</p>
        
        <h4>Ordering Problems</h4>
        <p><strong>Q: My payment was declined</strong></p>
        <p>A: Common causes include:</p>
        <ul>
          <li>Insufficient funds</li>
          <li>Card expired or blocked</li>
          <li>Billing address mismatch</li>
          <li>Daily spending limits reached</li>
          <li>Bank security holds</li>
        </ul>
        
        <p><strong>Q: I can't find my prison on the list</strong></p>
        <p>A: Not all prisons participate in AB Orders yet. Check our participating facilities page or contact the prison directly.</p>
        
        <p><strong>Q: Items are showing as out of stock</strong></p>
        <p>A: Stock levels update in real-time. Try refreshing the page or selecting alternative items.</p>
        
        <h4>Visit Day Issues</h4>
        <p><strong>Q: I can't find my order at the prison</strong></p>
        <p>A: Present your order confirmation to reception staff. They can locate your order using your details.</p>
        
        <p><strong>Q: My visit was cancelled - what about my order?</strong></p>
        <p>A: Prison-cancelled visits receive automatic full refunds. Visitor-cancelled visits follow our refund policy.</p>
        
        <h4>Technical Issues</h4>
        <p><strong>Q: The website is loading slowly</strong></p>
        <p>A: Try:</p>
        <ul>
          <li>Refreshing the page</li>
          <li>Clearing browser cache</li>
          <li>Using a different browser</li>
          <li>Checking your internet connection</li>
        </ul>
        
        <p><strong>Q: I'm getting error messages</strong></p>
        <p>A: Take a screenshot and contact our technical support team with details about what you were trying to do.</p>
        
        <h4>Getting Additional Help</h4>
        <ul>
          <li><strong>Live Chat:</strong> Available 8am-8pm daily</li>
          <li><strong>Phone Support:</strong> 0800-AB-ORDERS</li>
          <li><strong>Email Support:</strong> support@ab-orders.com</li>
          <li><strong>Emergency Contact:</strong> Available 24/7 for urgent issues</li>
        </ul>
        
        <div class="bg-yellow-50 dark:bg-yellow-900 p-4 rounded-lg mt-4">
          <strong>Tip:</strong> Keep your order confirmation number handy when contacting support for faster assistance.
        </div>
      `
    }
  ];

  const categories = [
    { id: 'all', name: 'All Topics', icon: FiInfo },
    { id: 'visitor', name: 'Visitors', icon: FiUsers },
    { id: 'prison', name: 'Prison Staff', icon: FiShield },
    { id: 'admin', name: 'Administrators', icon: FiSettings },
    { id: 'general', name: 'General', icon: FiInfo }
  ];

  const filteredArticles = useMemo(() => {
    return helpArticles.filter(article => {
      const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
      const matchesSearch = searchTerm === '' || 
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      return matchesCategory && matchesSearch;
    });
  }, [searchTerm, selectedCategory]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-government-50 to-primary-50 dark:from-government-900 dark:to-government-800 transition-colors duration-300">
      {/* Header */}
      <header className="bg-white/80 dark:bg-government-900/80 backdrop-blur-sm border-b border-government-200 dark:border-government-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="flex items-center space-x-2 text-government-600 dark:text-government-300 hover:text-government-900 dark:hover:text-white transition-colors"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
                <span>Back to Home</span>
              </Link>
              
              <div className="flex items-center space-x-3">
                <div className="bg-primary-500 p-2 rounded-lg">
                  <img 
                    src="https://res.cloudinary.com/frontly/image/upload/v1750023193/Untitled_design_eaqfik.svg" 
                    alt="AB Orders Logo" 
                    className="w-6 h-6"
                  />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-government-900 dark:text-white">How It Works</h1>
                  <p className="text-sm text-government-600 dark:text-government-300">Complete Guide & Help Center</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <ThemeToggle />
              <Link
                to="/login"
                className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search and Filter Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-government-900 dark:text-white mb-4">
              How AB Orders Works
            </h2>
            <p className="text-xl text-government-600 dark:text-government-300 max-w-3xl mx-auto">
              Find comprehensive guides and answers to help you make the most of the AB Orders prison pre-ordering system
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-government-400 dark:text-government-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search help articles..."
                className="w-full pl-12 pr-4 py-4 text-lg border border-government-300 dark:border-government-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-government-800 text-government-900 dark:text-white transition-colors duration-300"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-white dark:bg-government-800 text-government-600 dark:text-government-300 hover:bg-government-50 dark:hover:bg-government-700'
                }`}
              >
                <SafeIcon icon={category.icon} className="w-5 h-5" />
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12"
        >
          {[
            { icon: FiUsers, title: 'Active Visitors', value: '10,000+', color: 'text-blue-600' },
            { icon: FiShield, title: 'Partner Prisons', value: '150+', color: 'text-green-600' },
            { icon: FiClock, title: 'Avg. Time Saved', value: '45 min', color: 'text-purple-600' },
            { icon: FiCheckCircle, title: 'Success Rate', value: '99.8%', color: 'text-primary-600' }
          ].map((stat, index) => (
            <div key={index} className="bg-white dark:bg-government-800 rounded-xl shadow-lg p-6 text-center transition-colors duration-300">
              <SafeIcon icon={stat.icon} className={`w-8 h-8 ${stat.color} mx-auto mb-3`} />
              <div className="text-2xl font-bold text-government-900 dark:text-white mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-government-600 dark:text-government-300">
                {stat.title}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Help Articles */}
        <div className="space-y-6">
          {filteredArticles.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-government-800 rounded-xl shadow-lg p-12 text-center transition-colors duration-300"
            >
              <SafeIcon icon={FiSearch} className="w-16 h-16 text-government-300 dark:text-government-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-government-900 dark:text-white mb-2">
                No articles found
              </h3>
              <p className="text-government-600 dark:text-government-300">
                Try adjusting your search terms or selecting a different category
              </p>
            </motion.div>
          ) : (
            filteredArticles.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-government-800 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl"
              >
                <div
                  className="p-6 cursor-pointer"
                  onClick={() => toggleExpanded(article.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          article.category === 'visitor' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300' :
                          article.category === 'prison' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' :
                          article.category === 'admin' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300' :
                          'bg-government-100 dark:bg-government-700 text-government-800 dark:text-government-300'
                        }`}>
                          {categories.find(c => c.id === article.category)?.name}
                        </span>
                        {article.tags.map((tag, tagIndex) => (
                          <span key={tagIndex} className="px-2 py-1 rounded text-xs bg-government-100 dark:bg-government-700 text-government-600 dark:text-government-300">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="text-xl font-bold text-government-900 dark:text-white mb-2">
                        {article.title}
                      </h3>
                      <p className="text-government-600 dark:text-government-300">
                        {article.description}
                      </p>
                    </div>
                    <SafeIcon 
                      icon={expandedItems[article.id] ? FiChevronDown : FiChevronRight} 
                      className="w-6 h-6 text-government-400 dark:text-government-500 ml-4 transition-transform duration-200" 
                    />
                  </div>
                </div>

                {expandedItems[article.id] && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="border-t border-government-200 dark:border-government-600"
                  >
                    <div 
                      className="p-6 prose dark:prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: article.content }}
                    />
                  </motion.div>
                )}
              </motion.div>
            ))
          )}
        </div>

        {/* Contact Support Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16 bg-primary-50 dark:bg-primary-900/20 rounded-xl p-8 text-center transition-colors duration-300"
        >
          <h3 className="text-2xl font-bold text-government-900 dark:text-white mb-4">
            Still Need Help?
          </h3>
          <p className="text-government-600 dark:text-government-300 mb-6">
            Our support team is available 24/7 to assist with any questions about AB Orders
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center space-x-2 text-government-700 dark:text-government-300">
              <SafeIcon icon={FiPhone} className="w-5 h-5" />
              <span className="font-medium">0800-AB-ORDERS</span>
            </div>
            <div className="flex items-center space-x-2 text-government-700 dark:text-government-300">
              <SafeIcon icon={FiMail} className="w-5 h-5" />
              <span className="font-medium">support@ab-orders.com</span>
            </div>
          </div>
          <div className="mt-6">
            <Link
              to="/login"
              className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center space-x-2"
            >
              <span>Get Started with AB Orders</span>
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5 rotate-180" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default HowItWorks;